// Lead Nurture → team management (routes/nurture_team.py): today's team view, managing reps,
// the weekly rota (Shifts tab) and leave with a cover rep.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, UserPlus, AlertTriangle, CalendarOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  getNurtureTeamToday, getNurtureTeamMembers, searchNurtureCandidates, setNurtureMemberAccess, setNurtureMemberManager,
  saveNurtureMemberProfile, saveNurtureRota, saveNurtureOverride, deleteNurtureOverride, getNurtureRota, getNurtureTargets,
  saveNurtureTargets, listNurtureLeave, requestNurtureLeave, getNurtureLeaveImpact, approveNurtureLeave, rejectNurtureLeave,
  cancelNurtureLeave, getNurtureExotelPlan, refreshNurtureExotelUsers,
} from "@/lib/api";

const err = (e, fallback) => e?.response?.data?.detail || fallback;
const TZ_SHORT = { "Asia/Kolkata": "India time", "Asia/Colombo": "Sri Lanka time", "Asia/Dubai": "Dubai time" };
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TARGETS = [["calls", "Calls"], ["first_call_10min_pct", "New leads called in 10 min (%)"], ["booked", "Consults booked"], ["confirms", "Confirmations"]];
const shortGym = (name) => (name || "").split(" - ").pop();
const span = (s) => `${s.start}–${s.end}`;
const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
const selectCls = "h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm text-zinc-200";
const timeCls = "h-8 rounded-md border border-[#262626] bg-[#0b0b0b] px-1.5 text-xs text-zinc-200";

function Progress({ value, target, suffix = "" }) {
  if (target == null) return <span>{value ?? "—"}</span>;
  const ok = value != null && value >= target;
  return (
    <span className={ok ? "text-emerald-300 font-semibold" : value ? "text-zinc-200" : "text-zinc-500"}>
      {value ?? "—"}{suffix}<span className="text-zinc-500 font-normal">/{target}{suffix}</span>
    </span>
  );
}

// "Today: 34/60 calls · 3/5 booked · …" on the rep's board header.
export function TodayLine({ today }) {
  if (!today) return null;
  const p = today.progress || {}; const t = today.targets || {};
  const s = today.shift || {};
  return (
    <p className="text-xs text-zinc-400 mt-1" data-testid="today-targets">
      <b className="text-zinc-300">Today:</b> <Progress value={p.calls} target={t.calls} /> calls ·{" "}
      <Progress value={p.booked} target={t.booked} /> booked · <Progress value={p.confirms} target={t.confirms} /> confirmations ·{" "}
      {p.first_calls ? <><Progress value={p.first_call_10min_pct} target={t.first_call_10min_pct} suffix="%" /> new leads within 10 min</> : "no first calls yet"}
      {s.on_shift ? <span className="text-emerald-400"> · on shift until {s.until_local}{s.covering_for ? ` (covering ${s.covering_for})` : ""}</span>
        : s.next_local ? <span className="text-zinc-500"> · next shift {s.next_local}</span> : null}
    </p>
  );
}

// ---------------------------------------------------------------- Team tab: today + management

export function TeamToday() {
  const [d, setD] = useState(null);
  const load = useCallback(() => getNurtureTeamToday().then(setD).catch(() => setD({ reps: [] })), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  if (!d) return <div className="card-surface p-6 text-center text-sm text-zinc-500">Loading…</div>;
  return (
    <section className="card-surface p-4 space-y-3" data-testid="team-today">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Today</h3>
        <span className="text-[11px] text-zinc-500">
          Exotel switching: <b className={d.presence_mode === "on" ? "text-emerald-300" : "text-zinc-300"}>{d.presence_mode}</b>
          {d.pending_leave > 0 && <span className="text-amber-300"> · {d.pending_leave} leave request{d.pending_leave > 1 ? "s" : ""} waiting (Shifts tab)</span>}
        </span>
      </div>
      {(d.group_notes || []).map((n) => (
        <div key={n} className="rounded-md border border-amber-500/30 bg-amber-500/[0.07] px-3 py-2 text-xs text-amber-200 flex gap-2" data-testid="group-note">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /> {n}
        </div>
      ))}
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="w-full text-sm tabular-nums">
          <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
            <tr className="text-left">
              {["Rep", "Now", "Shifts today", "Calls", "Booked", "Confirmed", "In 10 min", "Flags", "Exotel"].map((h) => (
                <th key={h} className="px-2 py-2 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {d.reps.length === 0 && <tr><td colSpan={9} className="px-2 py-6 text-center text-zinc-500">No one on the team yet.</td></tr>}
            {d.reps.map((r) => {
              const st = r.state || {};
              const ph = r.exotel?.phone;
              return (
                <tr key={r.user_id} data-testid={`today-${r.user_id}`}>
                  <td className="px-2 py-2 whitespace-nowrap font-medium">
                    {r.name}{r.nurture_manager && <span className="ml-1.5 text-[10px] text-rose-300">manager</span>}
                    {r.tz !== "Asia/Kolkata" && <div className="text-[10px] text-zinc-500 font-normal">{TZ_SHORT[r.tz]}</div>}
                  </td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {r.leave.length > 0 && !st.on_shift ? <span className="text-violet-300">On leave</span>
                      : st.on_shift ? <span className="text-emerald-300">On shift · till {st.until_local}{st.covering_for ? <div className="text-[10px]">covering {st.covering_for}</div> : null}</span>
                        : <span className="text-zinc-500">Off{st.next_local ? ` · from ${st.next_local}` : ""}</span>}
                  </td>
                  <td className="px-2 py-2 text-xs text-zinc-400">
                    {r.shifts_today.length ? r.shifts_today.map((s) => (
                      <div key={s.start} className={s.kind === "cover" ? "text-sky-300" : ""}>{span(s)}{s.kind === "cover" ? ` (for ${s.for})` : ""}</div>
                    )) : "—"}
                    {r.leave.map((l) => <div key={l.id} className="text-violet-300">Leave {l.partial ? `${l.start_time}–${l.end_time}` : "all day"}{l.cover_name ? ` · ${l.cover_name} covers` : ""}</div>)}
                  </td>
                  <td className="px-2 py-2"><Progress value={r.progress.calls} target={r.targets.calls} /></td>
                  <td className="px-2 py-2"><Progress value={r.progress.booked} target={r.targets.booked} /></td>
                  <td className="px-2 py-2"><Progress value={r.progress.confirms} target={r.targets.confirms} /></td>
                  <td className="px-2 py-2 whitespace-nowrap">
                    {r.progress.first_calls ? <Progress value={r.progress.first_call_10min_pct} target={r.targets.first_call_10min_pct} suffix="%" /> : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-2 py-2 text-xs">
                    {r.late.length ? r.late.map((f) => <div key={f.start} className="text-amber-300" data-testid={`late-${r.user_id}`}>{f.text}</div>) : <span className="text-zinc-600">—</span>}
                  </td>
                  <td className="px-2 py-2 text-xs whitespace-nowrap">
                    {r.exotel ? (
                      <>
                        <div className={r.exotel.want_on ? "text-emerald-300" : "text-violet-300"}>{r.exotel.text}</div>
                        {ph?.exotel_number && <div className="text-[10px] text-zinc-500">Exotel phone {ph.exotel_number}</div>}
                        {ph?.mismatch && <div className="text-[10px] text-amber-300">Not the Exotel number in their My phones</div>}
                        {ph?.missing_in_fpa && <div className="text-[10px] text-amber-300">No Exotel phone in their My phones</div>}
                        {ph?.shared_with?.length > 0 && <div className="text-[10px] text-amber-300">Same phone as {ph.shared_with.join(", ")}</div>}
                        {!r.exotel.exotel_user_id && <div className="text-[10px] text-zinc-500">Not linked to Exotel</div>}
                      </>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-500">
        Times are each rep&apos;s own time. Flags: no call within {d.late_minutes} minutes of a shift starting (just for you to see; nobody is messaged).
        Exotel: everyone stays ON and Exotel&apos;s call flow does the shift timing; FPA switches a rep OFF only during approved leave (when switching is on).
      </p>
    </section>
  );
}

export function TeamManage() {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [adding, setAdding] = useState(false);
  const [plan, setPlan] = useState(null);
  const load = useCallback(() => {
    getNurtureTeamMembers().then(setD).catch(() => setD(null));
    getNurtureExotelPlan().then(setPlan).catch(() => setPlan(null));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const gymName = (id) => shortGym(d.facilities.find((f) => f.id === id)?.name) || id;
  const readExotel = async () => {
    try { const r = await refreshNurtureExotelUsers(); toast.success(`Read ${r.users} Exotel users`); load(); } catch (e) { toast.error(err(e, "Couldn't read Exotel")); }
  };
  return (
    <section className="card-surface p-4 space-y-3" data-testid="team-manage">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">The team</h3>
        {d.can_edit && (
          <div className="flex gap-2">
            {plan?.exotel_ready && <Button size="sm" variant="outline" onClick={readExotel}><RefreshCw className="h-4 w-4 mr-1.5" /> Read Exotel users</Button>}
            <Button size="sm" onClick={() => setAdding(true)} data-testid="add-rep-btn"><UserPlus className="h-4 w-4 mr-1.5" /> Add a rep</Button>
          </div>
        )}
      </div>
      <div className="divide-y divide-[#1a1a1a]">
        {d.members.map((m) => (
          <div key={m.user_id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`member-${m.user_id}`}>
            <div className="min-w-0">
              <span className="font-medium">{m.name}</span>
              {m.nurture_manager && <span className="ml-1.5 text-[10px] text-rose-300">Lead Nurture manager</span>}
              {!m.lead_nurture_access && !m.nurture_manager && <span className="ml-1.5 text-[10px] text-zinc-500">(no board access)</span>}
              <div className="text-[11px] text-zinc-500 truncate">
                {m.email} · {m.nurture_gyms.length ? m.nurture_gyms.map(gymName).join(", ") : "every gym"} · {TZ_SHORT[m.tz]}
                {m.exotel_user_id ? ` · Exotel ${m.exotel_user_id}/${m.exotel_device_id}` : ""}
              </div>
            </div>
            {d.can_edit && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEdit(m)} data-testid={`edit-${m.user_id}`}>Edit</Button>}
          </div>
        ))}
      </div>
      {d.can_edit && <TargetsEditor members={d.members} />}
      {!d.can_edit && <p className="text-[11px] text-zinc-500">Only the Lead Nurture manager and admins can change the team.</p>}
      <MemberDialog member={edit} data={d} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      <AddRepDialog open={adding} onOpenChange={setAdding} onAdded={load} />
    </section>
  );
}

function AddRepDialog({ open, onOpenChange, onAdded }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => searchNurtureCandidates(q).then((r) => setRows(r.users)).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q, open]);
  const add = async (u) => {
    try { await setNurtureMemberAccess(u.user_id, true); toast.success(`${u.name} is on the Lead Nurture team`); onOpenChange(false); onAdded(); }
    catch (e) { toast.error(err(e, "Couldn't add")); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-md">
        <DialogHeader>
          <DialogTitle>Add a rep</DialogTitle>
          <DialogDescription>Pick someone who already has an FPA login. They get the Lead Nurture board for every gym.</DialogDescription>
        </DialogHeader>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" autoFocus data-testid="rep-search" />
        <div className="max-h-72 overflow-y-auto divide-y divide-[#1a1a1a]">
          {rows.length === 0 && <div className="py-4 text-center text-xs text-zinc-500">No one found</div>}
          {rows.map((u) => (
            <button key={u.user_id} onClick={() => add(u)} className="w-full text-left py-2 px-1 hover:bg-[#151515]" data-testid={`candidate-${u.user_id}`}>
              <div className="text-sm">{u.name}</div><div className="text-[11px] text-zinc-500">{u.email}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MemberDialog({ member, data, onClose, onSaved }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (member) setF({ gyms: member.nurture_gyms, tz: member.tz, exu: member.exotel_user_id, exd: member.exotel_device_id });
  }, [member]);
  if (!member || !f) return null;
  const countries = [...new Set(data.facilities.map((x) => x.country))];
  const toggleGym = (id) => setF((x) => ({ ...x, gyms: x.gyms.includes(id) ? x.gyms.filter((g) => g !== id) : [...x.gyms, id] }));
  const sug = member.suggested_exotel;
  const save = async () => {
    try {
      await saveNurtureMemberProfile(member.user_id, { nurture_gyms: f.gyms, tz: f.tz, exotel_user_id: f.exu || "", exotel_device_id: f.exd || "" });
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(err(e, "Couldn't save")); }
  };
  const flag = async (fn, msg) => { try { await fn(); toast.success(msg); onSaved(); } catch (e) { toast.error(err(e, "Couldn't change")); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-lg">
        <DialogHeader>
          <DialogTitle>{member.name}</DialogTitle>
          <DialogDescription>{member.email}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs text-zinc-400 mb-1">Gyms they handle <span className="text-zinc-600">(none ticked = every gym; they can still show all on the board)</span></div>
            {countries.map((c) => (
              <div key={c} className="mb-1.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{c}</div>
                <div className="flex flex-wrap gap-1.5 mt-0.5">
                  {data.facilities.filter((x) => x.country === c).map((x) => (
                    <button key={x.id} onClick={() => toggleGym(x.id)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs ${f.gyms.includes(x.id) ? "border-rose-500 bg-rose-500/10 text-white" : "border-[#262626] text-zinc-400"}`}>
                      {shortGym(x.name)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <label className="block">
            <span className="text-xs text-zinc-400">Their time (shifts and leave are in this)</span>
            <select value={f.tz} onChange={(e) => setF({ ...f, tz: e.target.value })} className={`${selectCls} w-full mt-1`}>
              {Object.entries(data.tzs).map(([tz, c]) => <option key={tz} value={tz}>{c} ({tz})</option>)}
            </select>
          </label>
          <div>
            <div className="text-xs text-zinc-400 mb-1">Exotel user and device <span className="text-zinc-600">(used when Exotel&apos;s users list has no one with their email)</span></div>
            <div className="grid grid-cols-2 gap-2">
              <Input value={f.exu} onChange={(e) => setF({ ...f, exu: e.target.value })} placeholder="User id" />
              <Input value={f.exd} onChange={(e) => setF({ ...f, exd: e.target.value })} placeholder="Device id" />
            </div>
            {sug && (sug.exotel_user_id !== f.exu || sug.exotel_device_id !== f.exd) && (
              <button className="mt-1 text-[11px] text-sky-300 underline" onClick={() => setF({ ...f, exu: sug.exotel_user_id, exd: sug.exotel_device_id })}>
                Use the ids from the Exotel dashboard: {sug.exotel_user_id} / {sug.exotel_device_id}{sug.number ? ` (${sug.number})` : ""}
              </button>
            )}
          </div>
          {data.is_admin && (
            <Button size="sm" variant="outline" onClick={() => flag(() => setNurtureMemberManager(member.user_id, !member.nurture_manager),
              member.nurture_manager ? "No longer the Lead Nurture manager" : "Now the Lead Nurture manager")} data-testid="toggle-manager">
              {member.nurture_manager ? "Remove as Lead Nurture manager" : "Make Lead Nurture manager"}
            </Button>
          )}
        </div>
        <DialogFooter className="gap-2">
          {member.lead_nurture_access && (
            <Button variant="outline" className="text-rose-300" onClick={() => flag(() => setNurtureMemberAccess(member.user_id, false), `${member.name} is off the team`)}>
              Take off the team
            </Button>
          )}
          <Button onClick={save} data-testid="save-member">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TargetsEditor({ members }) {
  const [d, setD] = useState(null);
  useEffect(() => { getNurtureTargets().then(setD).catch(() => setD(null)); }, []);
  if (!d) return null;
  const set = (uid, k, v) => setD((x) => {
    const val = v === "" ? undefined : Number(v);
    if (!uid) return { ...x, default: { ...x.default, [k]: val } };
    return { ...x, reps: { ...x.reps, [uid]: { ...(x.reps[uid] || {}), [k]: val } } };
  });
  const save = async () => {
    try { await saveNurtureTargets({ default: d.default, reps: d.reps }); toast.success("Targets saved"); } catch (e) { toast.error(err(e, "Couldn't save")); }
  };
  const cell = (uid, k) => (
    <input type="number" min={0} value={(uid ? d.reps[uid]?.[k] : d.default[k]) ?? ""} onChange={(e) => set(uid, k, e.target.value)}
      placeholder={String(uid ? (d.default[k] ?? d.defaults[k]) : d.defaults[k])} aria-label={k}
      className="h-7 w-20 rounded-md border border-[#262626] bg-[#0b0b0b] px-1.5 text-xs text-zinc-200" />
  );
  return (
    <details className="pt-2" data-testid="targets-editor">
      <summary className="cursor-pointer text-sm font-semibold">Daily targets</summary>
      <p className="text-[11px] text-zinc-500 mt-1">Per day, in each rep&apos;s own time. Blank = the team default.</p>
      <div className="overflow-x-auto -mx-4 px-4 mt-2">
        <table className="text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
            <tr className="text-left"><th className="py-1 pr-3 font-medium">Who</th>{TARGETS.map(([k, l]) => <th key={k} className="py-1 pr-2 font-medium">{l}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            <tr><td className="py-1.5 pr-3 font-medium">Team default</td>{TARGETS.map(([k]) => <td key={k} className="py-1.5 pr-2">{cell(null, k)}</td>)}</tr>
            {members.map((m) => (
              <tr key={m.user_id}><td className="py-1.5 pr-3 whitespace-nowrap">{m.name}</td>{TARGETS.map(([k]) => <td key={k} className="py-1.5 pr-2">{cell(m.user_id, k)}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button size="sm" className="mt-2" onClick={save} data-testid="save-targets">Save targets</Button>
    </details>
  );
}

// ---------------------------------------------------------------- Shifts tab: rota grid + leave

function addDays(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export function ShiftsTab({ canManage }) {
  const [week, setWeek] = useState(null);
  const [d, setD] = useState(null);
  const [cell, setCell] = useState(null);         // {rep, day, idx}
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveTick, setLeaveTick] = useState(0);
  const load = useCallback(() => getNurtureRota(week).then(setD).catch((e) => toast.error(err(e, "Couldn't load the rota"))), [week]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); setLeaveTick((t) => t + 1); };
  if (!d) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="shifts-tab">
      <div className="flex flex-wrap items-center gap-2">
        <button className="h-8 w-8 grid place-items-center rounded-full border border-[#262626]" onClick={() => setWeek(addDays(d.week, -7))} aria-label="Previous week"><ChevronLeft className="h-4 w-4" /></button>
        <span className="text-sm font-medium">Week of {dayLabel(d.week)}</span>
        <button className="h-8 w-8 grid place-items-center rounded-full border border-[#262626]" onClick={() => setWeek(addDays(d.week, 7))} aria-label="Next week"><ChevronRight className="h-4 w-4" /></button>
        {week && <button className="text-xs text-zinc-400 underline" onClick={() => setWeek(null)}>This week</button>}
        <Button size="sm" variant="outline" className="ml-auto" onClick={() => setLeaveOpen(true)} data-testid="request-leave-btn">
          <CalendarOff className="h-4 w-4 mr-1.5" /> Request leave
        </Button>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-xs" data-testid="rota-grid">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium">Rep</th>
              {d.days.map((day, i) => <th key={day} className="px-2 py-2 font-medium whitespace-nowrap">{DAYS[i]} {new Date(`${day}T12:00:00`).getDate()}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {d.reps.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-zinc-500">No one on the team yet.</td></tr>}
            {d.reps.map((r) => (
              <tr key={r.user_id} className={r.user_id === d.me ? "bg-rose-500/[0.03]" : ""}>
                <td className="px-3 py-2 whitespace-nowrap font-medium">
                  {r.name}{r.user_id === d.me && <span className="text-[10px] text-rose-300 ml-1">you</span>}
                  {r.tz !== "Asia/Kolkata" && <div className="text-[10px] text-zinc-500 font-normal">{TZ_SHORT[r.tz]}</div>}
                </td>
                {r.days.map((c, i) => {
                  const approved = c.leave.find((l) => l.status === "approved");
                  const pending = c.leave.find((l) => l.status === "pending");
                  return (
                    <td key={c.date} className="px-1 py-1 align-top">
                      <button disabled={!d.can_edit} onClick={() => setCell({ rep: r, day: c, idx: i })} data-testid={`cell-${r.user_id}-${c.date}`}
                        className={`w-full min-w-[84px] min-h-[44px] text-left rounded-md border px-1.5 py-1 ${approved ? "border-violet-500/40 bg-violet-500/[0.07]" : c.override ? "border-sky-500/30" : "border-[#1f1f1f]"} ${d.can_edit ? "hover:border-zinc-500" : "cursor-default"}`}>
                        {c.shifts.length ? c.shifts.map((s) => (
                          <div key={s.start} className={approved && !approved.partial ? "line-through text-zinc-500" : "text-zinc-200"}>{span(s)}</div>
                        )) : <div className="text-zinc-600">off</div>}
                        {c.override && <div className="text-[10px] text-sky-300" title={c.override_note || undefined}>{c.override === "off" ? "day off (changed)" : c.override === "replace" ? "swapped" : "extra shift"}</div>}
                        {approved && <div className="text-[10px] text-violet-300">Leave {approved.partial ? `${approved.start_time}–${approved.end_time}` : ""}{approved.cover_name ? ` · ${approved.cover_name}` : ""}</div>}
                        {pending && <div className="text-[10px] text-amber-300">Leave asked</div>}
                        {c.covering.map((v) => <div key={v.leave_id + v.start} className="text-[10px] text-sky-300">Covers {v.for} {v.start}–{v.end}</div>)}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-zinc-500">
        Times are each rep&apos;s own time. {d.can_edit ? "Click a day to change the weekly pattern or just that date (day off, swap, extra shift)." : "Only the Lead Nurture manager can change shifts."}
      </p>
      <LeaveList canManage={canManage} tick={leaveTick} onChanged={refresh} />
      {cell && <CellDialog cell={cell} onClose={() => setCell(null)} onSaved={() => { setCell(null); load(); }} />}
      <LeaveRequestDialog open={leaveOpen} onOpenChange={setLeaveOpen} reps={d.can_edit ? d.reps : null} me={d.me} onSaved={refresh} />
    </div>
  );
}

function ShiftRows({ rows, setRows }) {
  return (
    <div className="space-y-1.5">
      {rows.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <input type="time" value={s.start} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, start: e.target.value } : x)))} className={timeCls} aria-label="From" />
          <span className="text-zinc-500">to</span>
          <input type="time" value={s.end} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} className={timeCls} aria-label="To" />
          <button onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove" className="text-zinc-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button onClick={() => setRows([...rows, { start: "09:00", end: "13:00" }])} className="text-xs text-zinc-400 inline-flex items-center gap-1 hover:text-zinc-200">
        <Plus className="h-3.5 w-3.5" /> Add a shift
      </button>
    </div>
  );
}

function CellDialog({ cell, onClose, onSaved }) {
  const { rep, day, idx } = cell;
  const [mode, setMode] = useState("week");
  const [weekRows, setWeekRows] = useState(() => (rep.week?.[String(idx)] || []).map((s) => ({ ...s })));
  const [kind, setKind] = useState(day.override || "off");
  const [dayRows, setDayRows] = useState(() => (day.override && day.override !== "off" ? day.shifts : []).map((s) => ({ ...s })));
  const [note, setNote] = useState(day.override_note || "");
  const save = async () => {
    try {
      if (mode === "week") {
        await saveNurtureRota(rep.user_id, { week: { ...rep.week, [String(idx)]: weekRows } });
      } else {
        await saveNurtureOverride(rep.user_id, day.date, { kind, shifts: kind === "off" ? [] : dayRows, note });
      }
      toast.success("Rota saved");
      onSaved();
    } catch (e) { toast.error(err(e, "Couldn't save")); }
  };
  const undo = async () => {
    try { await deleteNurtureOverride(rep.user_id, day.date); toast.success("Back to the weekly pattern"); onSaved(); } catch (e) { toast.error(err(e, "Couldn't undo")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-md">
        <DialogHeader>
          <DialogTitle>{rep.name} · {dayLabel(day.date)}</DialogTitle>
          <DialogDescription>Times in {TZ_SHORT[rep.tz]}.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2 text-sm">
          {[["week", `Every ${DAYS[idx]}`], ["day", "Only this date"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} className={`rounded-full px-3 py-1 border ${mode === k ? "border-rose-500 bg-rose-500/10 text-white" : "border-[#262626] text-zinc-400"}`}>{l}</button>
          ))}
        </div>
        {mode === "week" ? <ShiftRows rows={weekRows} setRows={setWeekRows} /> : (
          <div className="space-y-2">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className={`${selectCls} w-full`}>
              <option value="off">Day off</option>
              <option value="replace">Swap: these shifts instead</option>
              <option value="extra">Extra shift on top</option>
            </select>
            {kind !== "off" && <ShiftRows rows={dayRows} setRows={setDayRows} />}
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note, e.g. swapped with Indu" />
          </div>
        )}
        <DialogFooter className="gap-2">
          {mode === "day" && day.override && <Button variant="outline" onClick={undo}>Undo the change</Button>}
          <Button onClick={save} data-testid="save-cell">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeaveRequestDialog({ open, onOpenChange, reps, me, onSaved }) {
  const blank = useMemo(() => ({ user_id: me, kind: "annual", start_date: "", end_date: "", partial: false, start_time: "", end_time: "", reason: "" }), [me]);
  const [f, setF] = useState(blank);
  useEffect(() => { if (open) setF(blank); }, [open, blank]);
  const save = async () => {
    try {
      await requestNurtureLeave({ ...f, end_date: f.end_date || f.start_date, start_time: f.partial ? f.start_time : null, end_time: f.partial ? f.end_time : null });
      toast.success("Leave requested. The Lead Nurture manager decides and picks a cover.");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(err(e, "Couldn't request leave")); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-md">
        <DialogHeader>
          <DialogTitle>Request leave</DialogTitle>
          <DialogDescription>A full day, several days, or part of a day.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {reps && (
            <select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} className={`${selectCls} w-full`} aria-label="Who">
              {reps.map((r) => <option key={r.user_id} value={r.user_id}>{r.name}{r.user_id === me ? " (you)" : ""}</option>)}
            </select>
          )}
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={`${selectCls} w-full`} aria-label="Kind">
            {[["annual", "Annual leave"], ["sick", "Sick"], ["personal", "Personal"], ["bereavement", "Bereavement"], ["other", "Other"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} className={selectCls} aria-label="First day" />
            <span className="text-zinc-500">to</span>
            <input type="date" value={f.end_date} min={f.start_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} className={selectCls} aria-label="Last day" />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={f.partial} onChange={(e) => setF({ ...f, partial: e.target.checked })} /> Part of the day
          </label>
          {f.partial && (
            <div className="flex items-center gap-2">
              <input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} className={timeCls} aria-label="From" />
              <span className="text-zinc-500">to</span>
              <input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} className={timeCls} aria-label="To" />
            </div>
          )}
          <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Reason (optional)" />
        </div>
        <DialogFooter><Button onClick={save} disabled={!f.start_date} data-testid="save-leave">Request</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LeaveList({ tick, onChanged }) {
  const [d, setD] = useState(null);
  const [deciding, setDeciding] = useState(null);
  useEffect(() => { listNurtureLeave().then(setD).catch(() => setD({ leave: [] })); }, [tick]);
  if (!d) return null;
  const rows = d.leave.filter((l) => l.status === "pending" || l.status === "approved");
  const when = (l) => `${dayLabel(l.start_date)}${l.end_date !== l.start_date ? ` – ${dayLabel(l.end_date)}` : ""}${l.partial ? `, ${l.start_time}–${l.end_time}` : ""}`;
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); onChanged(); } catch (e) { toast.error(err(e, "Couldn't change")); } };
  return (
    <section className="card-surface p-4 space-y-2" data-testid="leave-list">
      <h3 className="text-sm font-semibold">Leave</h3>
      {rows.length === 0 && <p className="text-xs text-zinc-500">No leave asked for or coming up.</p>}
      <div className="divide-y divide-[#1a1a1a]">
        {rows.map((l) => (
          <div key={l.id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`leave-${l.id}`}>
            <div>
              <b>{l.user_name}</b> · {when(l)} <span className="text-zinc-500">· {l.kind}{l.reason ? ` · ${l.reason}` : ""}</span>
              <div className={`text-[11px] ${l.status === "pending" ? "text-amber-300" : "text-violet-300"}`}>
                {l.status === "pending" ? "Waiting for the Lead Nurture manager" : `Approved by ${l.decided_by}${l.covers?.calls ? ` · ${l.cover_names?.[l.covers.calls] || "cover"} covers` : " · no cover"}`}
              </div>
            </div>
            <div className="flex gap-1.5">
              {d.can_decide && <Button size="sm" className="h-7 text-xs" onClick={() => setDeciding(l)} data-testid={`decide-${l.id}`}>{l.status === "pending" ? "Approve" : "Change cover"}</Button>}
              {d.can_decide && l.status === "pending" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(() => rejectNurtureLeave(l.id, ""), "Turned down")}>Turn down</Button>}
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => act(() => cancelNurtureLeave(l.id, ""), "Leave cancelled")}>Cancel</Button>
            </div>
          </div>
        ))}
      </div>
      {deciding && <ApproveDialog leave={deciding} onClose={() => setDeciding(null)} onSaved={() => { setDeciding(null); onChanged(); }} />}
    </section>
  );
}

function ApproveDialog({ leave, onClose, onSaved }) {
  const [imp, setImp] = useState(null);
  const [cover, setCover] = useState(leave.covers?.calls || "");
  useEffect(() => { getNurtureLeaveImpact(leave.id).then(setImp).catch(() => setImp({ shifts: [], candidates: [] })); }, [leave.id]);
  const coverName = imp?.candidates.find((c) => c.user_id === cover)?.name;
  const save = async () => {
    try {
      await approveNurtureLeave(leave.id, { cover_user_id: cover || null, note: "" });
      toast.success(coverName ? `Approved. Add ${coverName} to ${leave.user_name}'s ring group in Exotel for these hours.` : "Approved");
      onSaved();
    } catch (e) { toast.error(err(e, "Couldn't approve")); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-md">
        <DialogHeader>
          <DialogTitle>{leave.user_name}&apos;s leave</DialogTitle>
          <DialogDescription>Pick who covers their shifts. While the leave runs, the cover counts as on shift for those hours.</DialogDescription>
        </DialogHeader>
        {!imp ? <div className="text-xs text-zinc-500">Loading…</div> : (
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-xs text-zinc-400 mb-1">Shifts it takes out ({TZ_SHORT[imp.tz] || imp.tz})</div>
              {imp.shifts.length ? imp.shifts.map((s) => <div key={s.date + s.start} className="text-xs">{dayLabel(s.date)} {s.start}–{s.end}</div>)
                : <div className="text-xs text-zinc-500">None: they have no shifts in these hours.</div>}
            </div>
            <select value={cover} onChange={(e) => setCover(e.target.value)} className={`${selectCls} w-full`} aria-label="Cover" data-testid="cover-select">
              <option value="">No cover</option>
              {imp.candidates.map((c) => <option key={c.user_id} value={c.user_id}>{c.name}{c.busy ? " (already on shift then)" : ""}</option>)}
            </select>
            {coverName && (
              <p className="text-[11px] text-amber-200">
                FPA switches {leave.user_name} OFF in Exotel for the leave (when switching is on), but can&apos;t change Exotel&apos;s groups:
                add {coverName} to {leave.user_name}&apos;s ring group in Exotel for these hours, and take them out after.
              </p>
            )}
          </div>
        )}
        <DialogFooter><Button onClick={save} disabled={!imp} data-testid="approve-leave">Approve</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
